import {KPICard} from "@/components/KPICard/KPICard.tsx";
import {cappedUptimePercentage, DurationFormatOptions, durationSecFn} from "@/lib/format.tsx";
import type {StatusPageTimeFormat} from "@/lib/types/statuspages.ts";
import {formatDistanceToNowStrict, isValid, parseISO} from "date-fns";

import type {ComponentDrilldownResult} from "../../types.ts";
import {formatPageDateTime} from "../../utils/dateTimeFormat.ts";
import styles from "./DrilldownKPICards.module.scss";

interface DrilldownKPICardsProps {
  drilldown: ComponentDrilldownResult;
  timezone: string;
  timeFormat?: StatusPageTimeFormat;
}

// Days once past 24 hours: a 246-hour outage reads "10 days 6 hours", not "246h 0m".
const formatDowntime = (secs: number | null): string =>
  secs ? durationSecFn(secs, DurationFormatOptions.LONG) : "None Recorded";

export const DrilldownKPICards = ({drilldown, timezone, timeFormat}: DrilldownKPICardsProps) => {
  const uptimeDisplay = drilldown.uptime_pct != null ? cappedUptimePercentage(drilldown.uptime_pct) : "N/A";
  const statusValueClass = drilldown.is_up ? styles.kpiValueGreen : styles.kpiValueRed;

  const outages = drilldown.outages ?? 0;
  // Legacy states when it happened rather than how long ago (latest_downtime.html).
  const lastDowntimeAt = drilldown.last_downtime_at ? parseISO(drilldown.last_downtime_at) : null;
  const lastDowntimeDisplay =
    lastDowntimeAt && isValid(lastDowntimeAt)
      ? formatPageDateTime(lastDowntimeAt, timezone, timeFormat)
      : "None Recorded";

  const alertsLabel = drilldown.last_alert_at
    ? `Alerts: Last: ${formatDistanceToNowStrict(new Date(drilldown.last_alert_at))}`
    : "Alerts";

  return (
    <div className={styles.kpiGrid}>
      <div className={styles.kpiRow}>
        <KPICard title="Current Status" variant="statuspage" valueClassName={statusValueClass}>
          {drilldown.current_status_display}
        </KPICard>
        <KPICard title="Uptime %" variant="statuspage" valueClassName={statusValueClass}>
          {uptimeDisplay}
        </KPICard>
        <KPICard title="Check Frequency" variant="statuspage">
          {/* check_interval is msp_interval — minutes, formatted as on the check analysis card. Zero
              means the check type has no polling interval (RUM, Incoming Webhook, Group, Cloud
              Status), which would otherwise render a dangling "Every". */}
          {drilldown.check_interval
            ? `Every ${durationSecFn(drilldown.check_interval * 60, DurationFormatOptions.LONG)}`
            : "N/A"}
        </KPICard>
      </div>
      <div className={styles.kpiRow}>
        <KPICard title={`Downtime - ${outages} outage${outages === 1 ? "" : "s"}`} variant="statuspage">
          {formatDowntime(drilldown.downtime_secs)}
        </KPICard>
        <KPICard title="Last Downtime" variant="statuspage">
          {lastDowntimeDisplay}
        </KPICard>
        <KPICard title={alertsLabel} variant="statuspage">
          {drilldown.alerts_count ?? 0}
        </KPICard>
      </div>
    </div>
  );
};
